// MOD-13 — Topbar bildirim zili BE client.
//
// BE endpoints (hepsi authenticated, RBAC izni YOK — yalnız çağıranın bildirimleri):
//   GET /api/v1/notifications?unreadOnly=&page=&pageSize=  → data.items[] + data.unreadCount + pagination
//   PUT /api/v1/notifications/{id}/read                    → ObjectActionResponse (data:{}, idempotent)
//   PUT /api/v1/notifications/read-all                     → data.markedCount
//
// Wire tip diğer bloklarla çakışmasın diye burada elle tanımlanır (generated
// api.ts'e dokunmuyoruz — Blok G/analytics deseni).

import { http, unwrap, type ActionResponse } from './http';
import { NormalizedApiError } from './errors';
import {
  NOTIFICATION_TYPES,
  type AppNotification,
  type NotificationFeed,
  type NotificationListQuery,
  type NotificationType,
} from '@features/notifications/types';

interface NotificationDtoWire {
  id?: string | null;
  type?: string | null;
  title?: string | null;
  body?: string | null;
  isRead?: boolean | null;
  readAt?: string | null;
  entityType?: string | null;
  entityRef?: string | null;
  createdAt?: string | null;
}

interface NotificationListDtoWire {
  items?: NotificationDtoWire[] | null;
  unreadCount?: number | null;
}

interface MarkAllReadResultWire {
  markedCount?: number | null;
}

const BASE = '/api/v1/notifications';

const KNOWN_TYPES = new Set<string>(NOTIFICATION_TYPES);

/** Bilinmeyen tür gelirse WorkflowSubmitted'e düşme yerine ham stringi korur; UI meta'sı
 * varsayılan ikonla karşılar. Böylece BE yeni bir tür eklerse FE kırılmaz. */
function normalizeType(raw: string | null | undefined): NotificationType {
  return (raw && KNOWN_TYPES.has(raw) ? raw : (raw ?? '')) as NotificationType;
}

function mapNotification(d: NotificationDtoWire): AppNotification {
  return {
    id: d.id ?? '',
    type: normalizeType(d.type),
    title: d.title ?? '',
    body: d.body ?? null,
    isRead: d.isRead ?? false,
    readAt: d.readAt ?? null,
    entityType: d.entityType ?? '',
    entityRef: d.entityRef ?? '',
    createdAt: d.createdAt ?? '',
  };
}

export const notificationsApi = {
  /** Çağıranın bildirimleri (yeni→eski) + rozet için tam okunmamış sayısı. */
  async list(query: NotificationListQuery = {}): Promise<NotificationFeed> {
    const params: Record<string, string | number | boolean> = {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 20,
    };
    if (query.unreadOnly !== undefined) params.unreadOnly = query.unreadOnly;
    const res = await http.get<ActionResponse<NotificationListDtoWire>>(BASE, { params });
    const data = unwrap(res);
    return {
      items: (data.items ?? []).map(mapNotification),
      unreadCount: data.unreadCount ?? 0,
    };
  },

  /** Tek bildirimi okundu işaretle (idempotent). Dönen `data:{}` kullanılmaz. */
  async markRead(id: string): Promise<void> {
    const res = await http.put<ActionResponse<unknown>>(`${BASE}/${id}/read`);
    if (res.data?.isSuccessful !== true) {
      throw new NormalizedApiError({
        status: res.status,
        code: res.data?.errors?.[0]?.code ?? `HTTP_${res.status}`,
        message: res.data?.message ?? 'Bildirim okundu işaretlenemedi.',
      });
    }
  },

  /** Tümünü okundu işaretle → işaretlenen adet (rozet 0'a düşer). */
  async markAllRead(): Promise<number> {
    const res = await http.put<ActionResponse<MarkAllReadResultWire>>(`${BASE}/read-all`);
    return unwrap(res).markedCount ?? 0;
  },
};
